import { useState } from 'react';
import { createApi } from 'unsplash-js';
import { getSiteRootPath } from '../../Services/common';
import { BizButton } from '../Mini/BizButton';
import {
	MediabankFile,
	MediabankFileSettings,
	MediabankFileTypes,
	MediabankUploadLocation,
	UnsplashPhoto,
	UnsplashColorType,
	OrientationType,
	MediabankSettings,
} from '../../Types/mediabank';
import { SelectedFileCallback } from './BizMediabankContext';
import { useMediabankMutations } from '../../Hooks/useMediabank';
import './biz-mediabank-unsplash.scss';

type MediabankDialogSelectUnsplashProps = {
	onClose: SelectedFileCallback;
	uploadLocation: MediabankUploadLocation;
	legacy?: boolean;
	orientation?: OrientationType;
	color?: UnsplashColorType;
	collectionIds?: string[];
	settings?: MediabankSettings;
};

const BizUnsplash = ({ onClose, uploadLocation, legacy, orientation, color, settings }: MediabankDialogSelectUnsplashProps) => {
	const [selected, setSelected] = useState<MediabankFile | null>(null);
	const [downloadUrl, setDownloadUrl] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const [results, setResults] = useState<UnsplashPhoto[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [isLinking, setIsLinking] = useState(false);
	const [hasMore, setHasMore] = useState(true);

	const { linkExternalFile } = useMediabankMutations();

	const unsplash = createApi({
		apiUrl: window.location.origin + getSiteRootPath() + 'api/mediabankuser/ProxyUnsplashRequest',
	});

	const okHandler = async () => {
		if (selected && !isLinking) {
			setIsLinking(true);
			try {
				await unsplash.photos.trackDownload({ downloadLocation: downloadUrl });

				linkExternalFile(
					selected.FileUrl,
					selected.Type,
					selected?.Thumbnail ?? '',
					uploadLocation,
					false,
					false,
					selected.Name,
					selected.Description || '',
					selected.Settings as MediabankFileSettings,
					selected.UrlRewrite || selected.Id.toString(),
					settings
				);

				onClose(selected);
			} catch (error) {
				console.error('Error during Unsplash download tracking or linking:', error);
			} finally {
				setIsLinking(false);
			}
		}
	};

	const setSelectedAsMbf = (image: UnsplashPhoto) => {
		const utm = '?utm_source=bizpart&utm_medium=referral';
		const userProfileLink = image.user.links?.html || '#';
		const photoLink = 'https://unsplash.com/';
		const descriptionHtml = `Photo by <a href="${userProfileLink}${utm}" target="_blank" rel="noopener noreferrer">${image.user.name}</a> on <a href="${photoLink}${utm}" target="_blank" rel="noopener noreferrer">Unsplash</a>`;

		const selectedFile: MediabankFile = {
			Id: -1,
			Name: image.alt_description || 'Unsplash image',
			Description: descriptionHtml,
			FileUrl: image.urls.regular.replace(/&w=\d+/, '&w=1920'),
			Thumbnail: image.urls.small,
			Settings: {
				ExternalId: image.id,
				ExternalAuthorName: image.user.name,
				ExternalAuthorUrl: image.user.links?.html || '',
			},
			Type: MediabankFileTypes.Image.id,
			UrlRewrite: image.id,
		};
		setSelected(selectedFile);
		setDownloadUrl(image.links.download_location);
	};

	const handleSearch = async (page = 1) => {
		if (!searchQuery || isLoading) return;

		setIsLoading(true);
		try {
			const response = await unsplash.search.getPhotos({
				query: searchQuery,
				page,
				perPage: 20,
				orientation,
				color,
			});

			if (response.response && response.response.results) {
				const newResults = response.response.results as UnsplashPhoto[];
				if (page === 1) {
					setResults(newResults);
				} else {
					setResults((prevResults) => [...prevResults, ...newResults]);
				}
				setCurrentPage(page);
				setHasMore(newResults.length > 0 && response.response.total_pages > page);
			} else {
				console.error('No results found or response is undefined');
				setResults([]);
				setHasMore(false);
			}
		} catch (error) {
			console.error('Error fetching Unsplash data:', error);
			setResults([]);
			setHasMore(false);
		} finally {
			setIsLoading(false);
		}
	};

	const loadMore = () => {
		if (hasMore && !isLoading) {
			handleSearch(currentPage + 1);
		}
	};

	return (
		<div className='unsplash-component'>
			<div className='unsplash-search'>
				<input
					type='text'
					placeholder='Search for images...'
					value={searchQuery}
					onChange={(e) => {
						setSearchQuery(e.target.value);
						setResults([]);
						setCurrentPage(1);
						setHasMore(true);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							handleSearch(1);
						}
					}}
				/>
				<BizButton
					onClick={() => handleSearch(1)}
					text={'Search'}
					isLead={searchQuery.trim().length > 0}
					disabled={isLoading}
					legacy={legacy}
				></BizButton>
			</div>

			<div className='unsplash-results'>
				{results.map((image) => (
					<div
						key={image.id}
						className={`unsplash-image ${selected?.Settings?.ExternalId === image.id ? 'selected' : ''}`}
					>
						<div
							className='unsplash-image-thumb'
							style={{ backgroundImage: `url(${image.urls.small})` }}
							onClick={() => (selected?.Settings?.ExternalId === image.id ? okHandler() : setSelectedAsMbf(image))}
						></div>
						<p>{image.description || image.alt_description || 'No description available'}</p>
					</div>
				))}
			</div>
			{isLoading && <div className='unsplash-loading'>Loading...</div>}
			{!isLoading && results.length > 0 && hasMore && (
				<div className='unsplash-load-more'>
					<BizButton onClick={loadMore} text='Load More' disabled={isLoading} />
				</div>
			)}
		</div>
	);
};

export default BizUnsplash;
